import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

export type { ButtonHTMLAttributes };
import { ChevronRight, Search, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ApplePrimitives — visionOS-inspired Liquid Glass + Spatial UI primitives.
 * Every surface has proper depth, contrast, and interaction feedback.
 */

// ─── Page Header ─────────────────────────────────────────────────
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className={cn('page-header', actions && 'with-actions')}>
      <div className="min-w-0">
        {eyebrow ? <div className="page-header-eyebrow">{eyebrow}</div> : null}
        <h1 className="page-header-title">{title}</h1>
        {subtitle ? <p className="page-header-subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-header-actions">{actions}</div> : null}
    </header>
  );
}

// ─── Card with depth + tint ───────────────────────────────────────
export function Card({
  className,
  children,
  tint,
  interactive,
  as: Tag = 'div',
  ...rest
}: HTMLAttributes<HTMLDivElement> & {
  tint?: 'pink' | 'blue' | 'green' | 'indigo' | 'orange' | 'purple' | 'gold' | 'rose' | 'amber' | 'emerald' | 'cyan' | 'yellow';
  interactive?: boolean;
  as?: 'div' | 'section' | 'article';
}) {
  return (
    <Tag
      className={cn(
        'card card-chrome',
        tint && `card-tint-${tint}`,
        interactive && 'card-interactive cursor-pointer',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// ─── Stat Tile ────────────────────────────────────────────────────
export function Stat({
  value,
  label,
  delta,
  deltaDir = 'flat',
  icon,
  tint = 'pink',
  className,
}: {
  value: ReactNode;
  label: ReactNode;
  delta?: ReactNode;
  deltaDir?: 'up' | 'down' | 'flat';
  tint?: 'pink' | 'blue' | 'green' | 'indigo' | 'orange' | 'purple' | 'gold' | 'yellow' | 'rose' | 'amber' | 'emerald' | 'cyan';
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <Card tint={tint} className={cn('stat', className)}>
      <div className="flex items-center justify-between">
        <span className="card-title">{label}</span>
        {icon ? (
          <span className={cn('stat-icon', `tint-${tint}`)}>{icon}</span>
        ) : null}
      </div>
      <div className="card-value">{value}</div>
      {delta ? (
        <span
          className={cn(
            'stat-delta',
            deltaDir === 'up' && 'stat-delta-up',
            deltaDir === 'down' && 'stat-delta-down',
            deltaDir === 'flat' && 'stat-delta-flat',
          )}
        >
          {delta}
        </span>
      ) : null}
    </Card>
  );
}

// ─── Grouped List (Settings.app style) ──────────────────────────
export function List({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('list', className)}>{children}</div>;
}

export function ListItem({
  title,
  subtitle,
  leading,
  leadingTint = 'blue',
  trailing,
  showChevron = false,
  onClick,
  noSeparator,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  leading?: ReactNode;
  leadingTint?: 'pink' | 'blue' | 'green' | 'orange' | 'purple' | 'indigo' | 'teal' | 'yellow';
  trailing?: ReactNode;
  showChevron?: boolean;
  onClick?: () => void;
  noSeparator?: boolean;
}) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={cn('list-item', noSeparator && '[&::before]:hidden')}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {leading ? (
        <span className={cn('list-item-leading', `tint-${leadingTint}`)}>
          {leading}
        </span>
      ) : null}
      <div className="list-item-content">
        <div className="list-item-title">{title}</div>
        {subtitle ? <div className="list-item-subtitle">{subtitle}</div> : null}
      </div>
      {trailing || showChevron ? (
        <div className="list-item-trailing">
          {trailing}
          {showChevron ? (
            <ChevronRight className="h-4 w-4 list-item-chevron" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ─── Segmented Control — iOS-style with sliding pink indicator ───
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  className,
}: {
  value: T;
  options: Array<{ value: T; label: ReactNode }>;
  onChange: (value: T) => void;
  className?: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ x: 0, w: 0 });

  // Measure the active item and update the sliding indicator.
  useEffect(() => {
    const measure = () => {
      const node = refs.current[value];
      const container = containerRef.current;
      if (!node || !container) return;
      const nodeRect = node.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      setIndicator({
        x: nodeRect.left - containerRect.left,
        w: nodeRect.width,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [value, options.length]);

  return (
    <div ref={containerRef} className={cn('segmented', className)} role="tablist">
      <span
        className="segmented-indicator"
        style={{
          transform: `translateX(${indicator.x}px)`,
          width: `${indicator.w}px`,
        }}
        aria-hidden
      />
      {options.map((opt) => (
        <button
          key={opt.value}
          ref={(el) => { refs.current[opt.value] = el; }}
          type="button"
          role="tab"
          aria-selected={value === opt.value}
          onClick={() => onChange(opt.value)}
          className={cn('segmented-item', value === opt.value && 'is-active')}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Tab Bar — page-level tabs with sliding pink underline ─────────
export function TabBar<T extends string>({
  value,
  options,
  onChange,
  className,
}: {
  value: T;
  options: Array<{ value: T; label: ReactNode }>;
  onChange: (value: T) => void;
  className?: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ x: 0, w: 0 });

  useEffect(() => {
    const measure = () => {
      const node = refs.current[value];
      const container = containerRef.current;
      if (!node || !container) return;
      const nodeRect = node.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      setIndicator({
        x: nodeRect.left - containerRect.left,
        w: nodeRect.width,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [value, options.length]);

  return (
    <div ref={containerRef} className={cn('tab-bar', className)} role="tablist">
      <span
        className="tab-bar-indicator"
        style={{
          transform: `translateX(${indicator.x}px)`,
          width: `${indicator.w}px`,
        }}
        aria-hidden
      />
      {options.map((opt) => (
        <button
          key={opt.value}
          ref={(el) => { refs.current[opt.value] = el; }}
          type="button"
          role="tab"
          aria-selected={value === opt.value}
          onClick={() => onChange(opt.value)}
          className={cn('tab-bar-item', value === opt.value && 'is-active')}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── iOS-style Toggle ────────────────────────────────────────────
export function Toggle({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={cn('apple-toggle cursor-pointer', checked && 'is-on')}
    />
  );
}

// ─── Apple-style Search ──────────────────────────────────────────
export function SearchField({
  value,
  onChange,
  placeholder = 'Search…',
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <label className={cn('search cursor-text', className)}>
      <Search className="h-3.5 w-3.5 shrink-0" />
      <input
        type="search"
        className="search-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

// ─── Buttons ──────────────────────────────────────────────────────
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'success' | 'outline' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

const variantClass: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  tertiary: 'btn-tertiary',
  destructive: 'btn-destructive',
  success: 'btn-success',
  outline: 'btn-secondary',
  ghost: 'btn-tertiary',
};

const sizeClass: Record<ButtonSize, string> = {
  sm: 'btn-sm',
  md: '',
  lg: 'btn-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
}>(function Button(
  { variant = 'secondary', size = 'md', iconOnly, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(
        'btn cursor-pointer',
        variantClass[variant],
        sizeClass[size],
        iconOnly && 'btn-icon',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

// ─── Empty / Skeleton / Spinner ───────────────────────────────────
export function Empty({
  icon,
  title,
  message,
  action,
}: {
  icon?: ReactNode;
  title: ReactNode;
  message?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon ? <div className="empty-icon">{icon}</div> : null}
      <div className="empty-title">{title}</div>
      {message ? <div className="empty-message">{message}</div> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function Skeleton({
  className,
  width,
  height,
}: {
  className?: string;
  width?: string | number;
  height?: string | number;
}) {
  return (
    <span
      className={cn('skeleton block', className)}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
      }}
    />
  );
}

export function Spinner({ className }: { className?: string }) {
  return <span className={cn('spinner inline-block', className)} aria-hidden />;
}

// ─── Form Fields ──────────────────────────────────────────────────
let fieldCounter = 0;
function uid(prefix: string) {
  fieldCounter += 1;
  return `${prefix}-${fieldCounter}`;
}

function FieldShell({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={htmlFor} className="field-label">
          {label}
          {required ? <span className="ml-1 text-pink-400">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <div className="field-error" role="alert">{error}</div>
      ) : hint ? (
        <div className="field-hint">{hint}</div>
      ) : null}
    </div>
  );
}

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, id, className, ...rest },
  ref,
) {
  const inputId = id ?? uid('apple-input');
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={inputId}>
      <input
        ref={ref}
        id={inputId}
        required={required}
        aria-invalid={!!error}
        className={cn('field-input', error && 'is-error', className)}
        {...rest}
      />
    </FieldShell>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, id, className, rows = 4, ...rest },
  ref,
) {
  const inputId = id ?? uid('apple-textarea');
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={inputId}>
      <textarea
        ref={ref}
        id={inputId}
        required={required}
        rows={rows}
        aria-invalid={!!error}
        className={cn('field-textarea resize-y', error && 'is-error', className)}
        {...rest}
      />
    </FieldShell>
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: Array<{ value: string; label: ReactNode; disabled?: boolean }>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, options, id, className, ...rest },
  ref,
) {
  const inputId = id ?? uid('apple-select');
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor={inputId}>
      <select
        ref={ref}
        id={inputId}
        required={required}
        aria-invalid={!!error}
        className={cn('field-select', error && 'is-error', className)}
        {...rest}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
});

// ─── Bento Grid container ────────────────────────────────────────
export function BentoGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('bento-grid', className)}>{children}</div>;
}